import React from "react";
import { Link } from "react-router-dom";
import SEO from "../../../components/SEO";
import img20 from "../../../assets/img20.webp";
import { CheckCircle } from "lucide-react";
import img10 from "../../../assets/img10.jpg";
import BlogSection from "../../../components/BlogSection";

export default function UniversalLifeInsurance() {
  return (
    <>
      <SEO
        title="Assurance vie universelle Canada | Aperçu police– einsured.ca"
        description="Explorez l'assurance vie universelle au Canada. Une police à vie qui combine protection et avantages de planification financière à long terme."
        keywords="assurance vie universelle Canada, assurance vie flexible, protection permanente, assurance vie à valeur de rachat"
      />

      {/* HERO SECTION */}
      <section
        className="relative min-h-[60vh] sm:min-h-[70vh] md:min-h-[80vh] bg-cover bg-center flex items-center justify-center md:justify-start px-4 sm:px-6 md:px-20"
        style={{ backgroundImage: `url(${img20})` }}
        role="img"
        aria-label="Bannière principale d'assurance vie universelle"
      >
        <div className="absolute inset-0 bg-black/40"></div>

        <div className="w-full max-w-[1380px] mx-auto">
          <div className="relative z-10 text-white max-w-2xl text-center md:text-left">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-3 md:mb-4 leading-tight">
              Assurance vie universelle
            </h1>
            <p className="text-base sm:text-lg md:text-xl font-semibold mb-6 md:mb-8">
              Protection à vie ajustable qui combine assurance et options de placement pour vous aider à faire croître votre patrimoine au fil du temps
            </p>
            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold text-lg px-8 py-3 rounded-full transition"
            >
              Commencer
            </a>
          </div>
        </div>
      </section>

      {/* UNDERSTANDING UNIVERSAL LIFE INSURANCE  */}
      <section className="py-14 sm:py-16 md:py-24 px-4 sm:px-6 md:px-24 bg-white text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#49628b] mb-6 md:mb-8">
              Comprendre l'assurance vie universelle
            </h2>

            <p className="text-base sm:text-lg leading-8 text-gray-700 mb-8 md:mb-10">
              Dans le monde complexe de la planification et de la sécurité financières,
              l'assurance vie universelle émerge comme un outil multidimensionnel conçu
              pour offrir non seulement un filet de sécurité pour vos proches en cas de
              décès prématuré, mais aussi un moyen d'accumuler une valeur de rachat au fil
              du temps. Ce système à double avantage positionne l'assurance vie universelle
              comme un choix convaincant pour ceux qui recherchent la flexibilité
              parallèlement à la sécurité à long terme.
            </p>

            <h3 className="text-2xl md:text-3xl font-bold text-[#49628b] mb-4 md:mb-6">
              Qu'est-ce que l'assurance vie universelle ?
            </h3>

            <p className="text-base sm:text-lg leading-8 text-gray-700 mb-10 md:mb-12">
              À la base, l'assurance vie universelle est un type d'{" "}
              <Link
                to="/fr/assurance-vie-permanente"
                className="text-sky-600 hover:underline"
              >
                assurance vie permanente
              </Link>
              , ce qui signifie qu'elle offre une couverture pour toute votre vie,
              contrairement à l'{" "}
              <Link
                to="/fr/assurance-vie-temporaire"
                className="text-sky-600 hover:underline"
              >
                assurance vie temporaire
              </Link>{" "}
              qui vous couvre pour un terme déterminé. Ce qui distingue l'assurance vie
              universelle, c'est sa structure de primes flexible et la capacité d'accumuler
              une valeur de rachat, offrant aux titulaires de police une solution ajustable
              adaptée à leur situation financière et à leurs objectifs en évolution.
            </p>

            {/* Key Features */}
            <h3 className="text-2xl md:text-3xl font-bold text-[#49628b] mb-8 md:mb-10">
              Caractéristiques clés de l'assurance vie universelle
            </h3>

            <div className="text-left space-y-6 text-base sm:text-lg leading-relaxed">
              <div className="flex gap-3 items-start">
                <CheckCircle
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <p>
                  <span className="font-bold text-[#49628b]">Flexibilité :</span>{" "}
                  Vous avez la flexibilité d'ajuster vos paiements de primes et vos
                  prestations de décès. Cela peut être particulièrement avantageux en
                  période d'incertitude ou de changement financier.
                </p>
              </div>

              <div className="flex gap-3 items-start">
                <CheckCircle
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <p>
                  <span className="font-bold text-[#49628b]">
                    Accumulation de valeur de rachat :
                  </span>{" "}
                  Une partie de votre prime contribue à un compte de valeur de rachat, qui
                  croît au fil du temps selon les taux d'intérêt de l'assureur ou la
                  performance du marché, selon votre type de police.
                </p>
              </div>

              <div className="flex gap-3 items-start">
                <CheckCircle
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <p>
                  <span className="font-bold text-[#49628b]">
                    Couverture à vie :
                  </span>{" "}
                  Tant que les primes sont payées, la couverture dure toute une vie,
                  offrant la tranquillité d'esprit et la sécurité financière à vos
                  bénéficiaires.
                </p>
              </div>

              <div className="flex gap-3 items-start">
                <CheckCircle
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <p>
                  <span className="font-bold text-[#49628b]">
                    Avantages fiscaux :
                  </span>{" "}
                  La valeur de rachat de la police croît à imposition différée, et les
                  bénéficiaires reçoivent généralement la prestation de décès libre d'impôt
                  fédéral sur le revenu.
                </p>
              </div>
            </div>

            {/* CTA Button */}
            <div className="mt-10 md:mt-12">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-sky-900 font-semibold text-lg px-10 py-4 rounded-full shadow-md transition"
              >
                Votre voie vers des primes abordables
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="pt-8 md:pt-2 pb-16 md:pb-20 px-4 sm:px-6 md:px-24 bg-white text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-6xl mx-auto">
            <p className="text-base md:text-lg leading-relaxed mb-6 md:mb-8 text-gray-700">
              Voici un tableau soulignant les différences entre l'assurance temporaire,
              l'assurance vie entière et l'assurance vie universelle :
            </p>

            {/* Mobile Responsive Cards */}
            <div className="md:hidden space-y-4">
              {[
                {
                  aspect: "Durée de couverture",
                  term: "Limitée (généralement 10 à 40 ans)",
                  whole: "Couverture à vie",
                  universal: "Couverture à vie",
                },
                {
                  aspect: "Primes",
                  term: "Généralement moins élevées, mais peuvent augmenter lors du renouvellement",
                  whole: "Plus élevées et fixes",
                  universal: "Flexibles, peuvent être ajustées",
                },
                {
                  aspect: "Valeur de rachat",
                  term: "Aucune valeur de rachat",
                  whole: "Constitue une valeur de rachat au fil du temps",
                  universal: "Constitue une valeur de rachat au fil du temps",
                },
                {
                  aspect: "Composante placement",
                  term: "Aucune composante placement",
                  whole: "Comprend une composante épargne/placement",
                  universal: "Comprend une composante épargne/placement",
                },
                {
                  aspect: "Flexibilité",
                  term: "Flexibilité limitée, période de couverture fixe",
                  whole: "Flexibilité limitée, primes fixes",
                  universal:
                    "Flexible, permet des ajustements de la couverture et des primes",
                },
                {
                  aspect: "Valeur de rachat nette",
                  term: "Aucune valeur de rachat nette",
                  whole: "A une valeur de rachat nette après une certaine période",
                  universal: "A une valeur de rachat nette après une certaine période",
                },
                {
                  aspect: "Avances sur police",
                  term: "Sans objet",
                  whole: "Peut contracter des avances sur police contre la valeur de rachat",
                  universal: "Peut contracter des avances sur police contre la valeur de rachat",
                },
                {
                  aspect: "Dividendes",
                  term: "Sans objet",
                  whole: "Peut recevoir des dividendes des bénéfices de l'assureur",
                  universal: "Peut recevoir des intérêts crédités à la valeur de rachat",
                },
                {
                  aspect: "Coût",
                  term: "Généralement moins cher",
                  whole: "Plus coûteux que l'assurance temporaire",
                  universal:
                    "Peut varier selon les primes et la performance des placements",
                },
                {
                  aspect: "Planification successorale",
                  term: "Souvent utilisée pour des besoins temporaires ou pour couvrir des dettes",
                  whole:
                    "Souvent utilisée pour des besoins permanents ou comme outil de planification successorale",
                  universal:
                    "Offre une combinaison de prestation de décès et d'opportunité de placement",
                },
                {
                  aspect: "Risque",
                  term: "Faible risque pour l'assureur car c'est pour un terme fixe",
                  whole:
                    "Risque modéré pour l'assureur en raison de la couverture à vie et de la valeur de rachat",
                  universal:
                    "Risque modéré pour l'assureur en raison de la couverture à vie et de la valeur de rachat, mais plus flexible pour le titulaire",
                },
              ].map((row) => (
                <div
                  key={row.aspect}
                  className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden"
                >
                  <div className="px-5 py-4 bg-[#f2f6fa]">
                    <p className="font-bold text-[#49628b]">{row.aspect}</p>
                  </div>

                  <div className="px-5 py-4 space-y-3 text-sm text-gray-800">
                    <div className="flex gap-3">
                      <span className="w-28 shrink-0 font-semibold text-[#49628b]">
                        Temporaire
                      </span>
                      <span className="flex-1">{row.term}</span>
                    </div>

                    <div className="flex gap-3">
                      <span className="w-28 shrink-0 font-semibold text-[#49628b]">
                        Entière
                      </span>
                      <span className="flex-1">{row.whole}</span>
                    </div>

                    <div className="flex gap-3">
                      <span className="w-28 shrink-0 font-semibold text-[#49628b]">
                        Universelle
                      </span>
                      <span className="flex-1">{row.universal}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/*  Desktop Table  */}
            <div className="hidden md:block">
              <div className="overflow-x-auto rounded-xl border border-gray-300">
                <table className="min-w-[880px] w-full border border-gray-300 text-left text-gray-800 text-sm md:text-base">
                  <thead className="bg-[#f2f6fa]">
                    <tr>
                      <th className="py-4 px-4 font-bold text-[#49628b] border border-gray-300">
                        Aspect
                      </th>
                      <th className="py-4 px-4 font-bold text-[#49628b] border border-gray-300">
                        <Link
                          to="/fr/assurance-vie-temporaire"
                          className="hover:underline"
                        >
                          Assurance vie temporaire
                        </Link>
                      </th>
                      <th className="py-4 px-4 font-bold text-[#49628b] border border-gray-300">
                        <Link
                          to="/fr/assurance-vie-entiere"
                          className="hover:underline"
                        >
                          Assurance vie entière
                        </Link>
                      </th>
                      <th className="py-4 px-4 font-bold text-[#49628b] border border-gray-300">
                        Assurance vie universelle
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-300">
                    <tr>
                      <td className="font-semibold py-4 px-4 border">
                        Durée de couverture
                      </td>
                      <td className="py-4 px-4 border">
                        Limitée (généralement 10 à 40 ans)
                      </td>
                      <td className="py-4 px-4 border">Couverture à vie</td>
                      <td className="py-4 px-4 border">Couverture à vie</td>
                    </tr>

                    <tr>
                      <td className="font-semibold py-4 px-4 border">
                        Primes
                      </td>
                      <td className="py-4 px-4 border">
                        Généralement moins élevées, mais peuvent augmenter lors du renouvellement
                      </td>
                      <td className="py-4 px-4 border">Plus élevées et fixes</td>
                      <td className="py-4 px-4 border">
                        Flexibles, peuvent être ajustées
                      </td>
                    </tr>

                    <tr>
                      <td className="font-semibold py-4 px-4 border">
                        Valeur de rachat
                      </td>
                      <td className="py-4 px-4 border">Aucune valeur de rachat</td>
                      <td className="py-4 px-4 border">
                        Constitue une valeur de rachat au fil du temps
                      </td>
                      <td className="py-4 px-4 border">
                        Constitue une valeur de rachat au fil du temps
                      </td>
                    </tr>

                    <tr>
                      <td className="font-semibold py-4 px-4 border">
                        Composante placement
                      </td>
                      <td className="py-4 px-4 border">
                        Aucune composante placement
                      </td>
                      <td className="py-4 px-4 border">
                        Comprend une composante épargne/placement
                      </td>
                      <td className="py-4 px-4 border">
                        Comprend une composante épargne/placement
                      </td>
                    </tr>

                    <tr>
                      <td className="font-semibold py-4 px-4 border">
                        Flexibilité
                      </td>
                      <td className="py-4 px-4 border">
                        Flexibilité limitée, période de couverture fixe
                      </td>
                      <td className="py-4 px-4 border">
                        Flexibilité limitée, primes fixes
                      </td>
                      <td className="py-4 px-4 border">
                        Flexible, permet des ajustements de la couverture et des primes
                      </td>
                    </tr>

                    <tr>
                      <td className="font-semibold py-4 px-4 border">
                        Valeur de rachat nette
                      </td>
                      <td className="py-4 px-4 border">Aucune valeur de rachat nette</td>
                      <td className="py-4 px-4 border">
                        A une valeur de rachat nette après une certaine période
                      </td>
                      <td className="py-4 px-4 border">
                        A une valeur de rachat nette après une certaine période
                      </td>
                    </tr>

                    <tr>
                      <td className="font-semibold py-4 px-4 border">
                        Avances sur police
                      </td>
                      <td className="py-4 px-4 border">Sans objet</td>
                      <td className="py-4 px-4 border">
                        Peut contracter des avances sur police contre la valeur de rachat
                      </td>
                      <td className="py-4 px-4 border">
                        Peut contracter des avances sur police contre la valeur de rachat
                      </td>
                    </tr>

                    <tr>
                      <td className="font-semibold py-4 px-4 border">
                        Dividendes
                      </td>
                      <td className="py-4 px-4 border">Sans objet</td>
                      <td className="py-4 px-4 border">
                        Peut recevoir des dividendes des bénéfices de l'assureur
                      </td>
                      <td className="py-4 px-4 border">
                        Peut recevoir des intérêts crédités à la valeur de rachat
                      </td>
                    </tr>

                    <tr>
                      <td className="font-semibold py-4 px-4 border">Coût</td>
                      <td className="py-4 px-4 border">Généralement moins cher</td>
                      <td className="py-4 px-4 border">
                        Plus coûteux que l'assurance temporaire
                      </td>
                      <td className="py-4 px-4 border">
                        Peut varier selon les primes et la performance des placements
                      </td>
                    </tr>

                    <tr>
                      <td className="font-semibold py-4 px-4 border">
                        Planification successorale
                      </td>
                      <td className="py-4 px-4 border">
                        Souvent utilisée pour des besoins temporaires ou pour couvrir des dettes
                      </td>
                      <td className="py-4 px-4 border">
                        Souvent utilisée pour des besoins permanents ou comme outil de planification successorale
                      </td>
                      <td className="py-4 px-4 border">
                        Offre une combinaison de prestation de décès et d'opportunité de placement
                      </td>
                    </tr>

                    <tr>
                      <td className="font-semibold py-4 px-4 border">Risque</td>
                      <td className="py-4 px-4 border">
                        Faible risque pour l'assureur car c'est pour un terme fixe
                      </td>
                      <td className="py-4 px-4 border">
                        Risque modéré pour l'assureur en raison de la couverture à vie et de la valeur de rachat
                      </td>
                      <td className="py-4 px-4 border">
                        Risque modéré pour l'assureur en raison de la couverture à vie et de la valeur de rachat, mais plus flexible pour le titulaire
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-base md:text-lg mt-8 md:mt-10 leading-relaxed text-gray-700">
              Ce tableau fournit un aperçu général, mais il est important de noter que les
              détails peuvent varier entre les compagnies d'assurance et les offres de
              polices.
            </p>
            <p className="text-base md:text-lg mt-4 leading-relaxed text-gray-700">
              Consultez un professionnel d'assurance agréé chez{" "}
              <Link to="/" className="text-sky-600 hover:underline font-medium">
                einsured.ca
              </Link>{" "}
              pour des conseils personnalisés.
            </p>

            {/* CTA Button */}
            <div className="text-center mt-8 md:mt-10">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-sky-900 font-semibold text-lg px-10 py-4 rounded-full shadow-md transition"
              >
                Réservez une réunion maintenant !
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* --- Choosing the Right Universal Life Insurance --- */}
      <section
        className="relative py-16 sm:py-20 md:py-28 px-4 sm:px-6 md:px-24 bg-cover bg-center md:bg-fixed"
        style={{ backgroundImage: `url(${img10})` }}
        role="img"
        aria-label="Image de fond pour la planification et les conseils en assurance vie universelle"
      >
        <div className="absolute inset-0 bg-sky-900 bg-opacity-75"></div>

        <div className="max-w-[1380px] mx-auto">
          <div className="relative z-10 max-w-6xl mx-auto text-center text-white">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-5 md:mb-6">
              Choisir la bonne assurance vie universelle
            </h2>

            <p className="max-w-4xl mx-auto text-base sm:text-lg leading-relaxed mb-8 md:mb-10 text-gray-100">
              Le choix de la police d'{" "}
              <span className="font-semibold">assurance vie universelle</span>{" "}
              la plus appropriée dépend de vos objectifs financiers, de votre tolérance au
              risque et de votre besoin de flexibilité.
            </p>

            <h3 className="text-xl sm:text-2xl font-semibold mb-8 md:mb-12 text-sky-200">
              Tenez compte des éléments suivants :
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16">
              <div className="bg-white/95 rounded-3xl p-8 shadow-lg hover:shadow-xl transition text-gray-800">
                <h4 className="text-2xl font-bold mb-4 text-[#49628b]">
                  Objectifs financiers :
                </h4>
                <p className="text-base leading-relaxed text-gray-700">
                  Évaluez vos objectifs financiers à long terme. Cherchez-vous un filet de
                  sécurité, un moyen de transmettre un patrimoine ou un moyen d'investir ?
                </p>
              </div>

              <div className="bg-white/95 rounded-3xl p-8 shadow-lg hover:shadow-xl transition text-gray-800">
                <h4 className="text-2xl font-bold mb-4 text-[#49628b]">
                  Tolérance au risque :
                </h4>
                <p className="text-base leading-relaxed text-gray-700">
                  Votre niveau de confort avec le risque déterminera quel type d'assurance
                  vie universelle est le plus approprié, des polices garanties plus stables
                  aux options variables potentiellement volatiles.
                </p>
              </div>

              <div className="bg-white/95 rounded-3xl p-8 shadow-lg hover:shadow-xl transition text-gray-800">
                <h4 className="text-2xl font-bold mb-4 text-[#49628b]">
                  Besoins en flexibilité :
                </h4>
                <p className="text-base leading-relaxed text-gray-700">
                  Évaluez le degré de flexibilité dont vous avez besoin en termes de
                  paiements de primes et de prestations de décès. Cela aura une incidence
                  sur votre choix et votre structure de police.
                </p>
              </div>
            </div>

            <div className="bg-white/95 rounded-3xl p-8 md:p-10 shadow-lg hover:shadow-xl transition text-gray-800 max-w-5xl mx-auto">
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-[#49628b]">
                L'avantage de la valeur de rachat
              </h3>

              <p className="text-base sm:text-lg leading-relaxed mb-6 md:mb-8 text-gray-700">
                L'une des caractéristiques distinctives de l'assurance vie universelle est
                sa composante de valeur de rachat, qui peut être utilisée de diverses
                façons :
              </p>

              <ul className="space-y-6 text-left text-gray-700 text-base sm:text-lg leading-relaxed max-w-4xl mx-auto">
                <li className="flex gap-3 items-start">
                  <CheckCircle
                    className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                    strokeWidth={2.5}
                  />
                  <p>
                    <span className="font-bold text-[#49628b]">
                      Emprunter ou retirer des fonds :
                    </span>{" "}
                    Les titulaires de police peuvent contracter des prêts ou effectuer des
                    retraits de la valeur de rachat accumulée, bien que cela puisse réduire
                    la prestation de décès.
                  </p>
                </li>

                <li className="flex gap-3 items-start">
                  <CheckCircle
                    className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                    strokeWidth={2.5}
                  />
                  <p>
                    <span className="font-bold text-[#49628b]">
                      Payer les primes :
                    </span>{" "}
                    Éventuellement, la valeur de rachat peut potentiellement couvrir vos
                    paiements de primes, offrant une forme de soulagement financier.
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* --- Navigating the Premiums --- */}
      <section className="py-14 sm:py-16 md:py-24 px-4 sm:px-6 md:px-24 bg-white text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#49628b] mb-6">
              Naviguer dans les primes
            </h2>

            <p className="text-base sm:text-lg leading-8 text-gray-700 mb-10 md:mb-14">
              Comprendre la structure et les implications des paiements de primes dans
              l'assurance vie universelle est crucial. La flexibilité d'ajuster les primes
              s'accompagne de la responsabilité de gérer activement la police pour éviter
              la déchéance. S'assurer que la valeur de rachat est suffisante pour maintenir
              la police tout en tenant compte de l'impact des taux d'intérêt et des
              conditions du marché est un équilibre délicat.
            </p>

            <h3 className="text-2xl md:text-3xl font-bold text-[#49628b] mb-6">
              L'assurance vie universelle est-elle faite pour vous ?
            </h3>

            <p className="text-base sm:text-lg leading-8 text-gray-700">
              L'assurance vie universelle n'est pas une solution universelle. Elle est
              idéale pour les personnes qui recherchent la permanence de l'{" "}
              <Link
                to="/fr/assurance-vie-entiere"
                className="text-sky-600 hover:underline font-medium"
              >
                assurance vie entière
              </Link>{" "}
              avec les avantages supplémentaires de la flexibilité et le potentiel de
              croissance de la valeur de rachat. Elle convient à ceux qui sont dans des
              situations financières variées, des jeunes professionnels qui planifient
              l'avenir aux retraités qui souhaitent laisser un héritage.
            </p>
          </div>
        </div>
      </section>

      {/* --- Final Thoughts --- */}
      <section className="relative bg-[#dbe3ed] border-y-4 border-[#2b4672] py-16 md:py-20 px-4 sm:px-6 md:px-24 text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#49628b] mb-6 md:mb-8">
              Réflexions finales
            </h2>

            <p className="text-base sm:text-lg leading-8 text-gray-700 mb-6">
              L'assurance vie universelle représente une composante polyvalente et puissante
              d'une stratégie financière complète. Sa capacité à s'adapter aux changements
              de la situation personnelle et financière tout en offrant un filet de sécurité
              en fait un produit unique sur le marché de l'assurance. Cependant, sa
              complexité nécessite une compréhension approfondie et une gestion proactive.
            </p>

            <p className="text-base sm:text-lg leading-8 text-gray-700">
              Consulter un conseiller financier chez{" "}
              <Link to="/" className="text-sky-600 hover:underline font-medium">
                einsured.ca
              </Link>{" "}
              peut fournir des conseils personnalisés adaptés à votre situation spécifique,
              en veillant à ce que la police choisie corresponde à vos objectifs financiers
              et offre la sécurité et la flexibilité dont vous avez besoin.
            </p>

            <div className="mt-8 md:mt-10">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-sky-900 font-semibold text-lg px-10 py-4 rounded-full shadow-md transition"
              >
                Planifiez une consultation gratuite
              </a>
            </div>
          </div>
        </div>
      </section>

      <BlogSection title="Derniers articles" mode="related" relatedCount={3} />
    </>
  );
}
